// WorkspaceNav — the Epic-style app shell chrome: a full-width "activity bar"
// of topics across the top, and a left "navigator" that swaps to the active
// topic's tools (replacing the classic sidebar). Pure navigation over the
// existing TOOLS / TOOL_SECTIONS registries; the routed tool renders in the
// body via the Layout's <Outlet/>. Gated behind a nav-mode toggle in Layout.
//
// Tier-filtered to match the classic Sidebar exactly (above-tier + admin-only
// tools never render; topics with no visible tools are hidden). Responsive:
// on mobile the navigator stacks under the activity bar as a horizontal strip.

import { useNavigate } from "react-router-dom";

import { TOOLS, TOOL_SECTIONS, type Tool, type ToolSectionId } from "@/data/tools";
import { cn } from "@/lib/utils";
import { useSubscriptionTier } from "@/hooks/useSubscriptionTier";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { canAccessTier, tierForPath } from "@/data/tierAccess";

// The topic tabs — content sections in sidebar order (skip dashboard/admin).
const BASE_SECTIONS = TOOL_SECTIONS
  .filter((s) => !["dashboard", "admin", "system-admin"].includes(s.id))
  .sort((a, b) => a.order - b.order);

/** Map the current pathname to the topic (section) it belongs to. */
export function sectionForPath(pathname: string): ToolSectionId | null {
  const tool = TOOLS.find((t) => t.path === pathname);
  if (tool) return tool.section;
  const hub = TOOL_SECTIONS.find((s) => s.hubPath && s.hubPath === pathname);
  if (hub) return hub.id;
  if (pathname.startsWith("/dashboard")) return "dashboard";
  return null;
}

/** Tier + admin filtered tool list — identical rules to the classic Sidebar. */
function useVisibleTools(): Tool[] {
  const { tier } = useSubscriptionTier();
  const { isAdmin } = useIsAdmin();
  return TOOLS.filter((t) => {
    if (!tier) return false;
    if (t.adminOnly && !isAdmin) return false;
    return canAccessTier(tier, tierForPath(t.path));
  });
}

/** Sections to show as tabs: those with a visible tool, or hub-only sections. */
function useVisibleSections() {
  const visibleTools = useVisibleTools();
  const sections = BASE_SECTIONS.filter((s) => {
    if (visibleTools.some((t) => t.section === s.id)) return true;
    const hubOnly = !TOOLS.some((t) => t.section === s.id) && !!s.hubPath;
    return hubOnly; // e.g. Cull & Bulls — the hub IS the tool
  });
  return { sections, visibleTools };
}

function landingPathFor(sectionId: ToolSectionId, visibleTools: Tool[]): string | null {
  const sec = TOOL_SECTIONS.find((s) => s.id === sectionId);
  if (sec?.hubPath) return sec.hubPath;
  return visibleTools.find((t) => t.section === sectionId)?.path ?? null;
}

// ── Full-width activity bar (the topics) ──────────────────────────────────
export function ActivityBar({ activeSection }: { activeSection: ToolSectionId | null }) {
  const navigate = useNavigate();
  const { sections, visibleTools } = useVisibleSections();
  return (
    <div className="w-full border-b bg-card print:hidden">
      <div className="flex overflow-x-auto">
        {sections.map((s) => {
          const active = s.id === activeSection;
          return (
            <button
              key={s.id}
              onClick={() => {
                const to = landingPathFor(s.id, visibleTools);
                if (to) navigate(to);
              }}
              className={cn(
                "whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors",
                active
                  ? "border-primary text-primary bg-muted/40"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/20",
              )}
            >
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Navigator (active topic's tools — replaces the sidebar) ────────────────
// Desktop: a fixed left rail. Mobile: a full-width horizontal strip that
// stacks under the activity bar (Layout switches the flex direction).
export function WorkspaceNavigator({
  activeSection,
  activePath,
}: {
  activeSection: ToolSectionId | null;
  activePath: string;
}) {
  const navigate = useNavigate();
  const { visibleTools } = useVisibleSections();
  const sec = TOOL_SECTIONS.find((s) => s.id === activeSection);
  const tools = visibleTools.filter((t) => t.section === activeSection);

  const item = (key: string, label: string, path: string) => {
    const active = activePath === path;
    return (
      <button
        key={key}
        onClick={() => navigate(path)}
        className={cn(
          // Mobile (horizontal strip): one line, no wrap. Desktop (vertical
          // rail): allow long tool names to wrap instead of clipping.
          "shrink-0 md:shrink whitespace-nowrap md:whitespace-normal md:leading-snug text-left px-4 py-2 text-sm transition-colors",
          // active indicator: bottom border on mobile, left border on desktop
          "border-b-2 md:border-b-0 md:border-l-2",
          active
            ? "border-primary bg-muted/50 text-foreground font-medium"
            : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/30",
        )}
      >
        {label}
      </button>
    );
  };

  return (
    <aside className="w-full md:w-60 shrink-0 border-b md:border-b-0 md:border-r bg-card print:hidden md:flex md:flex-col">
      {sec && (
        <div className="hidden md:block px-4 pt-3 pb-2 text-xs uppercase tracking-wider text-muted-foreground">
          {sec.label}
        </div>
      )}
      <div className="flex md:flex-col overflow-x-auto md:overflow-visible">
        {!activeSection && (
          <p className="px-4 py-3 text-xs text-muted-foreground">Pick a topic above.</p>
        )}
        {sec?.hubPath && item("__overview", "Overview", sec.hubPath)}
        {tools.map((t) => item(t.id, t.name, t.path))}
      </div>
      {/* Fill the remainder of the rail (desktop) so it reads as a full-height
          panel with a soft fade, not a list that abruptly cuts off. */}
      <div
        className="hidden md:block md:flex-1 md:min-h-12"
        style={{
          background:
            "linear-gradient(to bottom, hsl(var(--card)) 0%, hsl(var(--muted) / 0.25) 100%)",
        }}
        aria-hidden
      />
    </aside>
  );
}
